import type { ReactNode, RefObject } from 'react';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useScroll } from 'react-use';
import useLocation from 'react-use/lib/useLocation';

import { ScrollCollapseContext } from '~/components/ScrollCollapse/contexts/ScrollCollapseContext';
import { interpolate } from '~/components/ScrollCollapse/utils/interpolate';

interface ScrollCollapseProviderProps {
  children: ReactNode;
  // A reference to the scrollable element, typically a container with overflow.
  scrollRef: RefObject<HTMLElement>;
  // The scroll distance (in pixels) from 0px to 100% to calculate scroll progress.
  scrollDistanceMax?: number;
}

export function ScrollCollapseProvider({
  children,
  scrollRef,
  scrollDistanceMax = 100,
}: ScrollCollapseProviderProps) {
  const location = useLocation();
  const headerRef = useRef<HTMLDivElement>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const { y: scrollDistance } = useScroll(scrollRef);
  const scrollProgress = scrollDistance / scrollDistanceMax || 0;

  const getInterpolatedValue = useCallback(
    (max: number, min: number) => interpolate(max, min, scrollProgress),
    [scrollProgress],
  );

  const updateHeaderHeight = useCallback(() => {
    if (headerRef.current instanceof HTMLElement) {
      scrollRef.current?.style.setProperty(
        '--sticky-header-height',
        `${headerRef.current.offsetHeight || 0}px`,
      );
    }
  }, [headerRef, scrollRef]);

  // Observe the header element and invoke updateHeaderHeight when it resizes.
  const observeHeaderResize = useCallback(() => {
    if (!resizeObserverRef.current) {
      resizeObserverRef.current = new ResizeObserver(updateHeaderHeight);
    }

    if (headerRef.current instanceof HTMLElement) {
      resizeObserverRef.current.observe(headerRef.current);
    }
  }, [headerRef, updateHeaderHeight]);

  useEffect(() => {
    updateHeaderHeight();
    observeHeaderResize();

    return () => {
      resizeObserverRef.current?.disconnect();
    };
  }, [
    scrollDistance,
    headerRef,
    updateHeaderHeight,
    observeHeaderResize,
    location,
  ]);

  const value = useMemo(() => {
    return {
      scrollProgress,
      getInterpolatedValue,
      headerRef,
    };
  }, [scrollProgress, getInterpolatedValue]);

  return (
    <ScrollCollapseContext.Provider {...{ value }}>
      {children}
    </ScrollCollapseContext.Provider>
  );
}

ScrollCollapseProvider.displayName = 'ScrollCollapseProvider';
